import React from "react";
import {
  Card,
  CardContent,
  Grid,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import { Route, Routes, useParams } from "react-router-dom";
import Notices from "../admin/Notices";
import PageWithHeader from "../common/PageWithHeader";
import { Helmet } from "react-helmet-async";

import AnnouncementIcon from "@mui/icons-material/Announcement";
import ReceiptIcon from "@mui/icons-material/Receipt";
import HomeWorkIcon from "@mui/icons-material/HomeWork";
import PeopleIcon from "@mui/icons-material/People";
import BuildIcon from "@mui/icons-material/Assignment";
import BuildWrenchIcon from "@mui/icons-material/Build";
import DollarSign from "@mui/icons-material/AttachMoney";
import SportsTennisIcon from "@mui/icons-material/SportsTennis";
import MeetingRoomIcon from "@mui/icons-material/MeetingRoom";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import Properties from "../admin/Properties";
import Levies from "../admin/Levies";
import CreateInvoice from "../admin/CreateInvoice";
import CreateGenericInvoice from "../admin/CreateGenericInvoice";
import Products from "../admin/Items";
import PropertyInvoice from "../admin/PropertyInvoices";
import FinanceDashboard from "../admin/FinanceDashboard";
import LogManualPayment from "../admin/LogManualPayment";
import Users from "../admin/Users";
import PadelBookingsAdmin from "../admin/PadelBookingsAdmin";
import MaintenanceAdmin from "../admin/MaintenanceAdmin";
import AdminInvitesVisits from "../admin/AdminInvitesVisits";
import GenericInvoices from "../admin/GenericInvoices";
import { useRouter } from "../../hooks/useRouter";

const AdminDashboard: React.FC = () => {
  const router = useRouter();
  const params = useParams();

  const openAdminPage = (page: string) => {
    router.push(`/admin/${page}`);
  };

  return (
    <PageWithHeader>
      <Helmet>
        <title>Admin Portal | WestProp Residents Portal</title>
        <meta
          name="description"
          content="Administrative dashboard for managing properties, notices, levies, and more."
        />
      </Helmet>
      <Grid container spacing={5} sx={{ mt: 5 }}>
        <Grid item xs={12} md={3}>
          <Card raised>
            <CardContent>
              <Typography variant="h4" sx={{ py: 2 }}>
                Admin Portal
              </Typography>
              <Stack spacing={2}>
                <MenuItem
                  onClick={() => openAdminPage("property-invoices")}
                  selected={params?.[0] === "propertyInvoices"}
                >
                  <ListItemIcon>
                    <ReceiptIcon />
                  </ListItemIcon>
                  <ListItemText primary="Property Invoices" />
                </MenuItem>
                <MenuItem
                  onClick={() => openAdminPage("notices")}
                  selected={params?.[0] === "notices"}
                >
                  <ListItemIcon>
                    <AnnouncementIcon />
                  </ListItemIcon>
                  <ListItemText primary="Notices" />
                </MenuItem>
                <MenuItem
                  onClick={() => openAdminPage("levies")}
                  selected={params?.[0] === "levies"}
                >
                  <ListItemIcon>
                    <ReceiptIcon />
                  </ListItemIcon>
                  <ListItemText primary="Levies" />
                </MenuItem>
                <MenuItem
                  onClick={() => openAdminPage("properties")}
                  selected={params?.[0] === "properties"}
                >
                  <ListItemIcon>
                    <HomeWorkIcon />
                  </ListItemIcon>
                  <ListItemText primary="Properties" />
                </MenuItem>
                <MenuItem
                  onClick={() => openAdminPage("owners")}
                  selected={params?.[0] === "owners"}
                >
                  <ListItemIcon>
                    <PeopleIcon />
                  </ListItemIcon>
                  <ListItemText primary="Owners" />
                </MenuItem>
                <MenuItem
                  onClick={() => openAdminPage("products")}
                  selected={params?.[0] === "products"}
                >
                  <ListItemIcon>
                    <BuildIcon />
                  </ListItemIcon>
                  <ListItemText primary="Products" />
                </MenuItem>
                <MenuItem
                  onClick={() => openAdminPage("finance")}
                  selected={params?.[0] === "finance"}
                >
                  <ListItemIcon>
                    <DollarSign />
                  </ListItemIcon>
                  <ListItemText primary="Finance" />
                </MenuItem>
                <MenuItem
                  onClick={() => openAdminPage("log-manual-payment")}
                  selected={params?.[0] === "log-manual-payment"}
                >
                  <ListItemIcon>
                    <DollarSign />
                  </ListItemIcon>
                  <ListItemText primary="Log Manual Payment" />
                </MenuItem>
                <MenuItem
                  onClick={() => openAdminPage("padel-bookings")}
                  selected={params?.[0] === "padel-bookings"}
                >
                  <ListItemIcon>
                    <SportsTennisIcon />
                  </ListItemIcon>
                  <ListItemText primary="Padel Bookings" />
                </MenuItem>
                <MenuItem
                  onClick={() => openAdminPage("maintenance")}
                  selected={params?.[0] === "maintenance"}
                >
                  <ListItemIcon>
                    <BuildWrenchIcon />
                  </ListItemIcon>
                  <ListItemText primary="Maintenance" />
                </MenuItem>
                <MenuItem onClick={() => openAdminPage('invites-visits')} selected={params?.[0] === 'invites-visits'}>
                  <ListItemIcon><MeetingRoomIcon /></ListItemIcon>
                  <ListItemText primary="Invites & Visits" />
                </MenuItem>
                <MenuItem onClick={() => openAdminPage('generic-invoices')} selected={params?.[0] === 'generic-invoices'}>
                  <ListItemIcon><RequestQuoteIcon /></ListItemIcon>
                  <ListItemText primary="Generic Invoices" />
                </MenuItem>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={9}>
          <Routes>
            <Route path="/property-invoices" element={<PropertyInvoice />} />
            <Route path="/notices" element={<Notices />} />
            <Route path="/levies" element={<Levies />} />
            <Route path="/create-invoice" element={<CreateInvoice />} />
            <Route path="/properties" element={<Properties />} />
            <Route path="/owners" element={<Users />} />
            <Route path="/products" element={<Products />} />
            <Route path="/finance" element={<FinanceDashboard />} />
            <Route path="/log-manual-payment" element={<LogManualPayment />} />
            <Route path="/padel-bookings" element={<PadelBookingsAdmin />} />
            <Route path="/maintenance" element={<MaintenanceAdmin />} />
            <Route path="/invites-visits" element={<AdminInvitesVisits />} />
            <Route path="/generic-invoices" element={<GenericInvoices />} />
            <Route path="/create-generic-invoice" element={<CreateGenericInvoice />} />
          </Routes>
        </Grid>
      </Grid>
    </PageWithHeader>
  );
};

export default AdminDashboard;
